/* BrandLogos — Color-accurate inline SVG marks for the AI labs we connect to.
 *
 * Each mark uses the company's actual brand color and approximates the public
 * symbol closely enough to be instantly recognizable. SVGs are 24×24 viewBox
 * (Meta is 32×24 for aspect ratio) and accept a `className` for sizing.
 *
 * Designed to render on a dark background — no fills depend on `currentColor`
 * so they keep their brand color regardless of parent text color.
 */

export function AnthropicMark({ className }) {
  // Claude / Anthropic — radial sunburst in their signature orange
  const SPOKES = 12;
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      {Array.from({ length: SPOKES }, (_, i) => (i * 360) / SPOKES).map((angle) => (
        <rect
          key={angle}
          x="11.3"
          y="1.5"
          width="1.4"
          height="9.5"
          rx="0.7"
          fill="#CC785C"
          transform={`rotate(${angle} 12 12)`}
        />
      ))}
    </svg>
  );
}

export function OpenAIMark({ className }) {
  // OpenAI — official blossom logomark. Uses currentColor so it adapts to text
  // color (black on light bg, white on dark bg) and stays visible everywhere.
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" fill="currentColor">
      <path d="M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.872zm16.5963 3.8558L13.1038 8.364 15.1192 7.2a.0757.0757 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.407-.667zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.0804.0804 0 0 1-.038-.0567V6.0742a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.4997-2.6067-1.4997Z" />
    </svg>
  );
}

export function GeminiMark({ className }) {
  // Google Gemini — 4-point sparkle with the official multi-stop gradient
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <defs>
        <linearGradient id="cb-gemini-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#1C7CFF" />
          <stop offset="35%" stopColor="#9168F0" />
          <stop offset="70%" stopColor="#EA4FA9" />
          <stop offset="100%" stopColor="#F5A623" />
        </linearGradient>
      </defs>
      <path
        d="M12 1.6c.55 5.3 4.5 9.25 9.8 9.8c-5.3.55-9.25 4.5-9.8 9.8c-.55-5.3-4.5-9.25-9.8-9.8c5.3-.55 9.25-4.5 9.8-9.8Z"
        fill="url(#cb-gemini-gradient)"
      />
    </svg>
  );
}

export function MetaMark({ className }) {
  // Meta — interlocking infinity ribbon in their signature blue gradient
  return (
    <svg viewBox="0 0 32 24" className={className} aria-hidden="true" fill="none">
      <defs>
        <linearGradient id="cb-meta-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#0064E1" />
          <stop offset="50%" stopColor="#1877F2" />
          <stop offset="100%" stopColor="#0064E1" />
        </linearGradient>
      </defs>
      <path
        d="M5 12C5 7 7.5 4 11 4c2.7 0 4.5 1.5 6.5 4.5L21 14c1.5 2 2.7 3 4.2 3 1.7 0 2.8-1.4 2.8-3.2c0-2.3-1.4-5.2-3.3-5.2-1.4 0-2.7 1-4.2 3.3"
        stroke="url(#cb-meta-gradient)"
        strokeWidth="2.6"
        strokeLinecap="round"
      />
      <path
        d="M5 12c0 4.4 2 7 5.5 7c2.7 0 4.5-1.5 6.5-4.5"
        stroke="url(#cb-meta-gradient)"
        strokeWidth="2.6"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function PerplexityMark({ className }) {
  // Perplexity — official logomark geometry (from perplexity.ai SVG)
  // Color: #20808D (Perplexity's signature teal)
  return (
    <svg viewBox="0 0 28 36" className={className} aria-hidden="true" fill="none">
      <path
        d="m23.566,1.398l-9.495,9.504h9.495V1.398v2.602V1.398Zm-9.496,9.504L4.574,1.398v9.504h9.496Zm-.021-10.902v36m9.517-15.596l-9.495-9.504v13.625l9.495,9.504v-13.625Zm-18.991,0l9.496-9.504v13.625l-9.496,9.504v-13.625ZM.5,10.9v13.57h4.074v-4.066l9.496-9.504H.5Zm13.57,0l9.495,9.504v4.066h4.075v-13.57h-13.57Z"
        stroke="#20808D"
        strokeWidth="1"
        strokeMiterlimit="10"
      />
    </svg>
  );
}
